import { useState } from "react";
import { Download, Loader2, RotateCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { ConfirmarAccion } from "@/components/migracion/ConfirmarAccion";
import { EstadoLoteBadge } from "@/components/migracion/EstadoLoteBadge";
import { api, apiError } from "@/lib/api";
import { ESTADOS_CONTINUAR, consultasTxt, descargarArchivo } from "@/lib/migracion";

export function ProgresoLote({ lote, onCambio }) {
  const [preguntar, setPreguntar] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const c = lote.conteos || {};
  const total = (lote.filas_nuevas || 0) + (lote.filas_vinculadas || 0) + (lote.creditos?.validos || 0)
    + (lote.correcciones?.validos || 0);
  const hechas = (lote.procesadas || 0) + (lote.errores || 0);
  const enProceso = lote.estado === "en_proceso";
  const puedeContinuar = ESTADOS_CONTINUAR.includes(lote.estado);

  const continuar = async () => {
    setEnviando(true);
    try {
      await api.post(`/migracion-cartera/lotes/${lote.id}/confirmar`, {});
      toast.success("La migración continúa con las filas pendientes.");
      onCambio();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setEnviando(false);
    }
  };

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-5" data-testid="migracion-paso-progreso">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Paso 4</p>
          <h2 className="mt-1 text-base font-semibold text-slate-900">Avance de la migración</h2>
        </div>
        <EstadoLoteBadge estado={lote.estado} testid="migracion-progreso-estado" />
      </div>
      <Progress value={lote.progreso || 0} className="mt-3 h-2" data-testid="migracion-progreso-barra" />
      <p className="mt-2 text-sm text-slate-600" data-testid="migracion-progreso-texto">
        {hechas} de {total} procesadas{enProceso && " · se actualiza cada 4 s"}
      </p>
      {!enProceso && (
        <p className="mt-2 text-sm text-slate-700" data-testid="migracion-resumen-final">
          {c.creados || 0} nuevos · {c.vinculados || 0} vinculados · {c.errores || 0} con error
          {c.revision_pld > 0 && ` · ${c.revision_pld} en revisión PLD`}
          {c.pendientes_nuevo > 0 && ` · ${c.pendientes_nuevo} pendientes`}
          {lote.creditos?.total > 0 && (
            <span className="block" data-testid="migracion-resumen-final-creditos">
              Créditos: {c.creditos_creados || 0} migrados · {c.creditos_error || 0} con error
              {c.pendientes_credito > 0 && ` · ${c.pendientes_credito} pendientes`}
            </span>
          )}
          {lote.correcciones?.total > 0 && (
            <span className="block" data-testid="migracion-resumen-final-correcciones">
              Correcciones: {c.correcciones_aplicadas || 0} aplicadas · {c.correcciones_error || 0} con error
              {c.correcciones_omitidas > 0 && ` · ${c.correcciones_omitidas} omitidas`}
              {c.pendientes_correccion > 0 && ` · ${c.pendientes_correccion} pendientes`}
            </span>
          )}
        </p>
      )}
      {lote.error && !enProceso && <p className="mt-1 text-xs text-red-600" data-testid="migracion-progreso-error">{lote.error}</p>}
      {lote.estado === "detenido_sin_cupo" && lote.mensaje_bloqueo && (
        <p className="mt-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="migracion-progreso-bloqueo">{lote.mensaje_bloqueo}</p>
      )}
      {!enProceso && (
        <div className="mt-3 flex flex-wrap gap-2">
          <Button variant="outline" size="sm" className="gap-2" data-testid="migracion-descargar-reporte-final"
            onClick={() => descargarArchivo(`/migracion-cartera/lotes/${lote.id}/errores.xlsx`, `reporte_${lote.id}.xlsx`).catch((e) => toast.error(apiError(e)))}>
            <Download className="h-4 w-4" /> Descargar reporte
          </Button>
          {puedeContinuar && (
            <Button size="sm" className="gap-2" disabled={enviando} onClick={() => setPreguntar(true)} data-testid="migracion-continuar">
              {enviando ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCw className="h-4 w-4" />} Continuar
            </Button>
          )}
        </div>
      )}
      <ConfirmarAccion abierto={preguntar} onCerrar={() => setPreguntar(false)} titulo="Continuar la migración"
        accion="Continuar" onConfirmar={continuar} testid="migracion-continuar-dialogo">
        Se retoman solo las filas pendientes: {c.pendientes_nuevo || 0} altas (consumen consultas; disponibles:
        {" "}{consultasTxt(lote.consultas_disponibles_ahora)}), {c.pendientes_vincular || 0} vinculaciones, {c.pendientes_credito || 0} créditos
        {" "}y {c.pendientes_correccion || 0} correcciones.
        Lo ya procesado no se repite.
      </ConfirmarAccion>
    </section>
  );
}
